import React, { useState } from 'react';
import { Image, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import {
  AXIS_NAME_PRESETS,
  MAX_AXES,
  parseAxisValues,
  suggestInternalVariantBarcode,
  VARIANT_SIZE_PRESET_CHIPS,
  VARIANT_SIZE_PRESETS,
  extractAxisNames,
} from '../domain/variantMatrix';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';
import { NEGATIVE_PRICE_MESSAGE, rawPriceInputHadMinusSign } from '../domain/priceFieldGuard';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'draft'
  | 'hideStockPanels'
  | 'variantsEnabled'
  | 'variantDrafts'
  | 'parentStockQty'
  | 'currentBranchName'
  | 'onAddVariantDraft'
  | 'onUpdateVariantDraft'
  | 'onRemoveVariantDraft'
  | 'onGenerateVariantMatrix'
  | 'onPickVariantImage'
  | 'onRemoveVariantImage'
  | 'variantImageUploadingKey'
  | 'onPickAxisImage'
  | 'onRemoveAxisImage'
  | 'axisImageUploading'
  | 'inactiveVariants'
  | 'onReactivateVariant'
  | 'mobile'
  | 'desktopRail'
>;

type AxisRow = {
  id: string;
  name: string;
  values: string;
};

let axisRowCounter = 0;
function makeAxisRow(name = '', values = ''): AxisRow {
  axisRowCounter += 1;
  return { id: `axis-${axisRowCounter}`, name, values };
}

function axisLabel(name: string): string {
  const preset = AXIS_NAME_PRESETS.find((p) => p.name === name);
  if (preset) return preset.label;
  if (!name) return 'Atributo';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function CatalogProductFormVariantsFields({
  draft,
  hideStockPanels = false,
  variantsEnabled = false,
  variantDrafts = [],
  parentStockQty = null,
  currentBranchName,
  onAddVariantDraft,
  onUpdateVariantDraft,
  onRemoveVariantDraft,
  onGenerateVariantMatrix,
  onPickVariantImage,
  onRemoveVariantImage,
  variantImageUploadingKey,
  onPickAxisImage,
  onRemoveAxisImage,
  axisImageUploading,
  inactiveVariants = [],
  onReactivateVariant,
  mobile = false,
  desktopRail = false,
}: Props) {
  // Axis input rows for the matrix generator. Start with 1 empty row
  // so the user picks the attribute that makes sense for their product.
  const [axisRows, setAxisRows] = useState<AxisRow[]>([makeAxisRow()]);
  const [visualAxis, setVisualAxis] = useState<string>('');
  // Batch edit inputs
  const [batchPrice, setBatchPrice] = useState('');
  const [batchStock, setBatchStock] = useState('');
  // Per-variant "more options" toggle (SKU, barcode, image)
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(new Set());
  // Search filter for variant table
  const [variantSearch, setVariantSearch] = useState('');
  // El sanitizador decimalMoney descarta '-' en silencio ("-500" → 500):
  // se detecta el signo en el texto crudo (onChange) por campo de precio
  // para avisar en vez de guardar el valor absoluto sin aviso.
  const [minusPriceKeys, setMinusPriceKeys] = useState<Set<string>>(new Set());
  const trackMinusPrice = (key: string, raw: string) => {
    setMinusPriceKeys((prev) => {
      const had = rawPriceInputHadMinusSign(raw);
      if (had === prev.has(key)) return prev;
      const next = new Set(prev);
      if (had) next.add(key);
      else next.delete(key);
      return next;
    });
  };
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const showVariantEditor = variantsEnabled && draft.itemType === 'physical';

  if (!showVariantEditor) {
    return null;
  }

  // Extract axis names from existing variant drafts (for image panel).
  const existingAxisNames = extractAxisNames(variantDrafts);
  const activeVisualAxis = visualAxis || existingAxisNames[0] || '';

  // Unique values for the active visual axis.
  const visualAxisValues = activeVisualAxis
    ? Array.from(
        new Set(
          variantDrafts
            .map((d) => d.attributes?.[activeVisualAxis]?.trim())
            .filter(Boolean) as string[],
        ),
      )
    : [];

  const axisImageForValue = (axis: string, value: string): string | null => {
    const match = variantDrafts.find(
      (d) => d.attributes?.[axis] === value && d.imageUrl,
    );
    return match?.imageUrl ?? null;
  };

  const showAxisImagePanel = existingAxisNames.length > 0 && visualAxisValues.length > 0;

  const updateAxisRow = (id: string, patch: Partial<AxisRow>) => {
    setAxisRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addAxisRow = () => {
    if (axisRows.length >= MAX_AXES) return;
    setAxisRows((prev) => [...prev, makeAxisRow()]);
  };
  const removeAxisRow = (id: string) => {
    setAxisRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const handleGenerate = () => {
    onGenerateVariantMatrix?.(axisRows.map((r) => ({ name: r.name, values: r.values })));
  };

  // At least one axis must have a name and at least one value to generate.
  const hasValidAxis = axisRows.some(
    (r) => r.name.trim().length > 0 && r.values.trim().length > 0,
  );

  // Preview how many variants will be generated.
  const previewVariantCount = axisRows.reduce((acc, r) => {
    const count = parseAxisValues(r.values).length;
    return count > 0 ? acc * count : acc;
  }, 1);
  const LARGE_MATRIX_THRESHOLD = 20;
  const showLargeMatrixWarning = hasValidAxis && previewVariantCount > LARGE_MATRIX_THRESHOLD;

  const handleGenerateWithConfirm = async () => {
    if (showLargeMatrixWarning) {
      const ok = await confirm({
        title: 'Generar combinaciones',
        message: `Se crearán ${previewVariantCount} variantes. ¿Querés continuar?`,
        confirmLabel: 'Generar',
      });
      if (!ok) return;
    }
    handleGenerate();
  };

  // Batch edit: apply a value to all variant drafts.
  const applyPriceToAll = () => {
    const trimmed = batchPrice.trim();
    if (!trimmed) return;
    for (const row of variantDrafts) {
      onUpdateVariantDraft?.(row.key, { price: trimmed });
    }
  };
  const applyStockToAll = () => {
    const trimmed = batchStock.trim();
    if (!trimmed) return;
    // Opción A: Only apply to new variants (no id). Existing variants manage stock from inventory.
    for (const row of variantDrafts) {
      if (!row.id) onUpdateVariantDraft?.(row.key, { stockQty: trimmed });
    }
  };

  // Toggle "more options" for a specific variant.
  const toggleVariantExpanded = (key: string) => {
    setExpandedVariants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Detect duplicate axis names (case-insensitive).
  const axisNameCounts = new Map<string, number>();
  for (const r of axisRows) {
    const key = r.name.trim().toLowerCase();
    if (!key) continue;
    axisNameCounts.set(key, (axisNameCounts.get(key) ?? 0) + 1);
  }
  const duplicateAxisName = Array.from(axisNameCounts.entries()).find(
    ([, count]) => count > 1,
  )?.[0];

  // Show stock transition hint when the product had parent stock and now has variants.
  const showStockTransitionHint =
    !hideStockPanels && parentStockQty != null && parentStockQty > 0 && variantDrafts.length > 0;

  // C5: Calculate how much stock will be lost if variant cells don't sum to parent stock.
  const variantStockSum = variantDrafts.reduce((sum, vd) => {
    const qty = Number(vd.stockQty?.replace(',', '.') || 0);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);
  const stockLossAmount =
    showStockTransitionHint && parentStockQty != null
      ? Math.max(0, parentStockQty - variantStockSum)
      : 0;

  // Filtered variants for table view search.
  const filteredDrafts = variantSearch.trim()
    ? variantDrafts.filter((row) => {
        const attrEntries = Object.entries(row.attributes).filter(([, v]) => v?.trim());
        const variantTitle = attrEntries.map(([, v]) => v).join(' · ') || row.nameSuffix || 'Variante';
        const haystack = `${variantTitle} ${row.sku} ${row.barcode}`.toLowerCase();
        return haystack.includes(variantSearch.trim().toLowerCase());
      })
    : variantDrafts;

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Variantes</Text> : null}

      {showStockTransitionHint ? (
        <View
          style={{
            backgroundColor: stockLossAmount > 0 ? ticketColors.warnBg : ticketColors.bg,
            borderRadius: 8,
            padding: 10,
            marginBottom: 10,
            borderWidth: 1,
            borderColor: stockLossAmount > 0 ? ticketColors.warnBorder : ticketColors.ruleSoft,
          }}
        >
          <Text style={{ fontSize: 12, color: stockLossAmount > 0 ? ticketColors.warn : ticketColors.ink2, lineHeight: 18 }}>
            Stock actual del producto{currentBranchName ? ` (${currentBranchName})` : ''}: {Math.round(parentStockQty!)} unidades. Al guardar, el stock
            se gestiona por variante. Cargá la cantidad correspondiente a cada una.
            {stockLossAmount > 0
              ? `\n\n⚠ Si no distribuís las ${Math.round(stockLossAmount)} unidades restantes entre las variantes, se perderán al guardar.`
              : ''}
          </Text>
        </View>
      ) : null}

      {/* ── Step 1: Atributos ── */}
      <View style={styles.variantMatrixPanel}>
        <Text style={styles.ticketFormLabel}>Atributos del producto</Text>
        <Text style={[styles.inputHelper, { marginBottom: 8 }]}>
          Elegí qué variantes tiene el producto (hasta {MAX_AXES}). Los valores se separan por coma.
        </Text>

        {axisRows.map((row, index) => (
          <View
            key={row.id}
            style={{
              marginTop: index > 0 ? 10 : 0,
              paddingBottom: 10,
              borderBottomWidth: index < axisRows.length - 1 ? 1 : 0,
              borderBottomColor: ticketColors.ruleSoft,
            }}
          >
            {/* Preset chips */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              {AXIS_NAME_PRESETS.map((preset) => {
                const isActive = row.name === preset.name;
                return (
                  <TouchableOpacity
                    key={preset.name}
                    style={[
                      styles.variantPresetChip,
                      isActive && { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
                    ]}
                    onPress={() => {
                      updateAxisRow(row.id, {
                        name: preset.name,
                        values: preset.values ? preset.values.join(', ') : '',
                      });
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Atributo ${preset.label}`}
                  >
                    <Text
                      style={[
                        styles.variantPresetChipText,
                        isActive && { color: '#fff' },
                      ]}
                    >
                      {preset.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Name + remove button */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <TextInput
                style={[styles.ticketFormInput, { flex: 1 }]}
                value={row.name}
                onChangeText={(v) => updateAxisRow(row.id, { name: v })}
                placeholder="Nombre del atributo (ej: Sabor)"
                placeholderTextColor={ticketColors.ink3}
                autoCapitalize="none"
              />
              {axisRows.length > 1 ? (
                <TouchableOpacity
                  onPress={() => removeAxisRow(row.id)}
                  accessibilityRole="button"
                  accessibilityLabel="Eliminar atributo"
                  hitSlop={8}
                  style={{ padding: 10, minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                >
                  <Text style={{ fontSize: 16, color: ticketColors.ink3 }}>✕</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            {/* Values input */}
            <TextInput
              style={[styles.ticketFormInput, { marginTop: 6 }]}
              value={row.values}
              onChangeText={(v) => updateAxisRow(row.id, { values: v })}
              placeholder="Valores separados por coma (ej: S, M, L, XL)"
              placeholderTextColor={ticketColors.ink3}
            />
            <Text style={[styles.inputHelper, { marginTop: 4 }]}>
              Separá cada valor con una coma. Ej: Rojo, Azul, Negro
            </Text>

            {/* Quick-fill presets for talle */}
            {row.name === 'talle' ? (
              <View style={[styles.variantPresetRow, { marginTop: 6 }]}>
                {VARIANT_SIZE_PRESET_CHIPS.map(({ key, label }) => (
                  <TouchableOpacity
                    key={key}
                    style={styles.variantPresetChip}
                    onPress={() =>
                      updateAxisRow(row.id, {
                        values: VARIANT_SIZE_PRESETS[key].join(', '),
                      })
                    }
                    accessibilityRole="button"
                    accessibilityLabel={label}
                  >
                    <Text style={styles.variantPresetChipText}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : null}
          </View>
        ))}

        {axisRows.length < MAX_AXES ? (
          <TouchableOpacity
            style={[posUi.secondaryButton, { marginTop: 8, paddingVertical: 6 }]}
            onPress={addAxisRow}
            accessibilityRole="button"
            accessibilityLabel="Agregar atributo"
          >
            <Text style={posUi.secondaryButtonText}>+ Agregar atributo</Text>
          </TouchableOpacity>
        ) : null}

        {duplicateAxisName ? (
          <Text style={[styles.inputHelper, { color: ticketColors.bad ?? '#c0392b', marginTop: 6 }]}>
            Hay dos atributos con el nombre "{axisLabel(duplicateAxisName)}". Usá nombres distintos.
          </Text>
        ) : null}
        {showLargeMatrixWarning ? (
          <Text style={[styles.inputHelper, { color: ticketColors.bad ?? '#c0392b', marginTop: 6 }]}>
            Se crearán {previewVariantCount} variantes. Revisá antes de confirmar.
          </Text>
        ) : null}

        {/* Generate button + preview count */}
        {hasValidAxis && !duplicateAxisName ? (
          <Text style={[styles.inputHelper, { marginTop: 8, textAlign: 'center' }]}>
            Se {previewVariantCount === 1 ? 'creará 1 variante' : `crearán ${previewVariantCount} variantes`}
          </Text>
        ) : null}
        <TouchableOpacity
          style={[
            posUi.secondaryButton,
            styles.variantMatrixButton,
            { marginTop: 6 },
            (!hasValidAxis || duplicateAxisName) && { opacity: 0.4 },
          ]}
          onPress={handleGenerateWithConfirm}
          disabled={!hasValidAxis || !!duplicateAxisName}
          accessibilityRole="button"
          accessibilityLabel="Generar matriz de variantes"
          accessibilityHint={
            !hasValidAxis
              ? 'Elegí un atributo y cargá al menos un valor para habilitar'
              : undefined
          }
        >
          <Text style={posUi.secondaryButtonText}>Generar combinaciones</Text>
        </TouchableOpacity>
      </View>

      {/* ── Axis image panel: upload one image per attribute value ── */}
      {showAxisImagePanel ? (
        <View style={styles.variantMatrixPanel}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <Text style={styles.ticketFormLabel}>Imagen por atributo</Text>
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {existingAxisNames.map((name) => (
                <TouchableOpacity
                  key={name}
                  style={[
                    styles.variantPresetChip,
                    activeVisualAxis === name && { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
                  ]}
                  onPress={() => setVisualAxis(name)}
                >
                  <Text
                    style={[
                      styles.variantPresetChipText,
                      activeVisualAxis === name && { color: '#fff' },
                    ]}
                  >
                    {axisLabel(name)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <Text style={styles.inputHelper}>
            Subí una foto para cada {axisLabel(activeVisualAxis).toLowerCase()}. Se aplica a todas
            sus variantes.
          </Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {visualAxisValues.map((value) => {
              const imgUrl = axisImageForValue(activeVisualAxis, value);
              const uploadKey = `${activeVisualAxis}:${value}`;
              const isUploading = axisImageUploading === uploadKey;
              return (
                <View key={value} style={{ alignItems: 'center', gap: 4 }}>
                  {imgUrl ? (
                    <Image
                      source={{ uri: imgUrl }}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        backgroundColor: ticketColors.bg,
                      }}
                      resizeMode="cover"
                    />
                  ) : (
                    <TouchableOpacity
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        backgroundColor: ticketColors.bg,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: 1,
                        borderColor: ticketColors.ruleSoft,
                      }}
                      onPress={() => onPickAxisImage?.(activeVisualAxis, value)}
                      disabled={isUploading}
                      accessibilityRole="button"
                      accessibilityLabel={`Subir imagen para ${value}`}
                    >
                      <Text style={{ fontSize: 18, color: ticketColors.ink3 }}>+</Text>
                    </TouchableOpacity>
                  )}
                  <Text
                    style={{
                      fontSize: 10,
                      color: ticketColors.ink2,
                      maxWidth: 60,
                      textAlign: 'center',
                    }}
                    numberOfLines={1}
                  >
                    {value}
                  </Text>
                  {imgUrl ? (
                    <View style={{ flexDirection: 'row', gap: 4 }}>
                      <TouchableOpacity
                        onPress={() => onPickAxisImage?.(activeVisualAxis, value)}
                        disabled={isUploading}
                        hitSlop={8}
                        style={{ paddingVertical: 6 }}
                        accessibilityRole="button"
                        accessibilityLabel={`Cambiar imagen de ${value}`}
                      >
                        <Text style={{ fontSize: 10, color: ticketColors.gold }}>
                          {isUploading ? '…' : 'Cambiar'}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => onRemoveAxisImage?.(activeVisualAxis, value)}
                        hitSlop={8}
                        style={{ paddingVertical: 6 }}
                        accessibilityRole="button"
                        accessibilityLabel={`Quitar imagen de ${value}`}
                      >
                        <Text style={{ fontSize: 10, color: ticketColors.ink3 }}>Quitar</Text>
                      </TouchableOpacity>
                    </View>
                  ) : isUploading ? (
                    <Text style={{ fontSize: 10, color: ticketColors.ink3 }}>Subiendo…</Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        </View>
      ) : null}

      {/* ── Step 2: Variantes generadas ── */}
      {variantDrafts.length > 0 ? (
        <View style={styles.variantMatrixPanel}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={styles.ticketFormLabel}>Variantes ({variantDrafts.length})</Text>
            {variantDrafts.length > 5 ? (
              <Text style={[styles.inputHelper, { fontSize: 10 }]}>
                {filteredDrafts.length} de {variantDrafts.length}
              </Text>
            ) : null}
          </View>

          {/* Search bar (only when >5 variants) */}
          {variantDrafts.length > 5 ? (
            <TextInput
              style={styles.variantTableSearchInput}
              value={variantSearch}
              onChangeText={setVariantSearch}
              placeholder="Buscar variante..."
              placeholderTextColor={ticketColors.ink3}
              autoCapitalize="none"
            />
          ) : null}

          {/* Acciones rápidas */}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 4, marginBottom: 8 }}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.ticketFormLabel, { fontSize: 11 }]}>Precio para todas</Text>
              <NumericTextInput
                kind="decimalMoney"
                style={styles.ticketFormInput}
                value={batchPrice}
                onChangeText={setBatchPrice}
                onChange={(e) => trackMinusPrice('batch-price', e.nativeEvent.text)}
                placeholder="Ej: 2500"
                placeholderTextColor={ticketColors.ink3}
              />
              {minusPriceKeys.has('batch-price') ? (
                <Text style={[styles.inputHelper, styles.inputHelperError]}>{NEGATIVE_PRICE_MESSAGE}</Text>
              ) : null}
            </View>
            <TouchableOpacity
              style={[posUi.secondaryButton, { paddingVertical: 8, paddingHorizontal: 12, alignSelf: 'flex-end' }]}
              onPress={applyPriceToAll}
              disabled={!batchPrice.trim()}
              accessibilityRole="button"
              accessibilityLabel="Aplicar precio a todas las variantes"
            >
              <Text style={posUi.secondaryButtonText}>Aplicar</Text>
            </TouchableOpacity>
          </View>
          {!hideStockPanels ? (
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.ticketFormLabel, { fontSize: 11 }]}>Stock para todas</Text>
                <NumericTextInput
                  kind="integer"
                  style={styles.ticketFormInput}
                  value={batchStock}
                  onChangeText={setBatchStock}
                  placeholder="Ej: 10"
                  placeholderTextColor={ticketColors.ink3}
                />
              </View>
              <TouchableOpacity
                style={[posUi.secondaryButton, { paddingVertical: 8, paddingHorizontal: 12, alignSelf: 'flex-end' }]}
                onPress={applyStockToAll}
                disabled={!batchStock.trim()}
                accessibilityRole="button"
                accessibilityLabel="Aplicar stock a todas las variantes"
              >
                <Text style={posUi.secondaryButtonText}>Aplicar</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* ── Compact table view (>5 variants) ── */}
      {variantDrafts.length > 5 ? (
        <View style={styles.variantTable}>
          {/* Header */}
          <View style={styles.variantTableHeader}>
            <Text style={[styles.variantTableHeaderCell, { flex: 1.5 }]}>Variante</Text>
            <Text style={[styles.variantTableHeaderCell, { flex: 1 }]}>Precio</Text>
            {!hideStockPanels ? (
              <Text style={[styles.variantTableHeaderCell, { flex: 0.8 }]}>Stock{currentBranchName ? ` · ${currentBranchName}` : ''}</Text>
            ) : null}
            <Text style={[styles.variantTableHeaderCell, { width: 40 }]}></Text>
          </View>
          {/* Rows */}
          {filteredDrafts.map((row) => {
            const attrEntries = Object.entries(row.attributes).filter(([, v]) => v?.trim());
            const variantTitle = attrEntries.map(([, v]) => v).join(' · ') || row.nameSuffix || 'Variante';
            return (
              <View key={row.key} style={styles.variantTableRow}>
                <View style={{ flex: 1.5, justifyContent: 'center' }}>
                  <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink }} numberOfLines={1}>
                    {variantTitle}
                  </Text>
                  {row.sku ? (
                    <Text style={{ fontSize: 10, color: ticketColors.ink3 }} numberOfLines={1}>
                      SKU: {row.sku}
                    </Text>
                  ) : null}
                </View>
                <View style={{ flex: 1 }}>
                  <NumericTextInput
                    kind="decimalMoney"
                    style={styles.variantTableInput}
                    value={row.price}
                    onChangeText={(value) => onUpdateVariantDraft?.(row.key, { price: value })}
                    onChange={(e) => trackMinusPrice(row.key, e.nativeEvent.text)}
                    placeholder="-"
                    placeholderTextColor={ticketColors.ink3}
                  />
                  {minusPriceKeys.has(row.key) ? (
                    <Text style={[styles.inputHelper, styles.inputHelperError, { fontSize: 10 }]}>
                      {NEGATIVE_PRICE_MESSAGE}
                    </Text>
                  ) : null}
                </View>
                {!hideStockPanels ? (
                  <View style={{ flex: 0.8 }}>
                    <NumericTextInput
                      kind="integer"
                      style={[styles.variantTableInput, row.id ? { opacity: 0.5 } : null]}
                      value={row.stockQty}
                      onChangeText={(value) => onUpdateVariantDraft?.(row.key, { stockQty: value })}
                      placeholder="0"
                      placeholderTextColor={ticketColors.ink3}
                      editable={!row.id}
                      accessibilityLabel={row.id ? 'Stock gestionado desde inventario' : 'Stock inicial'}
                    />
                  </View>
                ) : null}
                <TouchableOpacity
                  onPress={async () => {
                    const ok = await confirm({
                      title: 'Eliminar variante',
                      message: `¿Eliminar la variante "${variantTitle}"?`,
                      confirmLabel: 'Eliminar',
                      destructive: true,
                    });
                    if (ok) onRemoveVariantDraft?.(row.key);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Quitar variante"
                  style={{ width: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                >
                  <Text style={{ fontSize: 14, color: ticketColors.ink3 }}>✕</Text>
                </TouchableOpacity>
              </View>
            );
          })}
          {filteredDrafts.length === 0 ? (
            <View style={{ padding: 16, alignItems: 'center' }}>
              <Text style={{ fontSize: 12, color: ticketColors.ink3 }}>
                No se encontraron variantes con "{variantSearch}"
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* ── Detailed card view (≤5 variants) ── */}
      {variantDrafts.length > 0 && variantDrafts.length <= 5 ? (
        <>
          {variantDrafts.map((row) => {
            const attrEntries = Object.entries(row.attributes).filter(([, v]) => v?.trim());
            const isExpanded = expandedVariants.has(row.key);
            const variantTitle = attrEntries.map(([, v]) => v).join(' · ') || row.nameSuffix || 'Variante';
            return (
              <View key={row.key} style={styles.variantCard}>
                {/* Header con título de la variante */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: ticketColors.ink, flexShrink: 1 }}>
                    {variantTitle}
                  </Text>
                  <TouchableOpacity
                    onPress={async () => {
                      const ok = await confirm({
                        title: 'Eliminar variante',
                        message: `¿Eliminar la variante "${variantTitle}"?`,
                        confirmLabel: 'Eliminar',
                        destructive: true,
                      });
                      if (ok) onRemoveVariantDraft?.(row.key);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Quitar variante"
                    hitSlop={8}
                    style={{ padding: 10, minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Text style={{ fontSize: 14, color: ticketColors.ink3 }}>✕</Text>
                  </TouchableOpacity>
                </View>

                {/* Dynamic attributes display/edit */}
                {attrEntries.length > 0 ? (
                  <View style={[styles.variantRow, { marginTop: 8 }]}>
                    {attrEntries.map(([key, val]) => (
                      <View
                        key={key}
                        style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}
                      >
                        <Text style={styles.ticketFormLabel}>{axisLabel(key)}</Text>
                        <TextInput
                          style={styles.ticketFormInput}
                          value={val}
                          onChangeText={(value) =>
                            onUpdateVariantDraft?.(row.key, {
                              attributes: { ...row.attributes, [key]: value },
                            })
                          }
                          placeholder={axisLabel(key)}
                          placeholderTextColor={ticketColors.ink3}
                        />
                      </View>
                    ))}
                  </View>
                ) : null}

                {/* Nombre + Precio + Stock */}
                <View style={[styles.variantRow, { marginTop: 8 }]}>
                  <View style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}>
                    <Text style={styles.ticketFormLabel}>Nombre</Text>
                    <TextInput
                      style={styles.ticketFormInput}
                      value={row.nameSuffix}
                      onChangeText={(value) => onUpdateVariantDraft?.(row.key, { nameSuffix: value })}
                      placeholder="Ej: M · Rojo"
                      placeholderTextColor={ticketColors.ink3}
                    />
                  </View>
                  <View style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}>
                    <Text style={styles.ticketFormLabel}>Precio</Text>
                    <NumericTextInput
                      kind="decimalMoney"
                      style={styles.ticketFormInput}
                      value={row.price}
                      onChangeText={(value) => onUpdateVariantDraft?.(row.key, { price: value })}
                      onChange={(e) => trackMinusPrice(row.key, e.nativeEvent.text)}
                      placeholder="Opcional"
                      placeholderTextColor={ticketColors.ink3}
                    />
                    {minusPriceKeys.has(row.key) ? (
                      <Text style={[styles.inputHelper, styles.inputHelperError, { marginTop: 2 }]}>
                        {NEGATIVE_PRICE_MESSAGE}
                      </Text>
                    ) : null}
                  </View>
                </View>
                {!hideStockPanels ? (
                  <View style={[styles.variantRow, { marginTop: 8 }]}>
                    <View style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}>
                      <Text style={styles.ticketFormLabel}>Stock{currentBranchName ? ` · ${currentBranchName}` : ''}</Text>
                      <NumericTextInput
                        kind="integer"
                        style={[styles.ticketFormInput, row.id ? { opacity: 0.5 } : null]}
                        value={row.stockQty}
                        onChangeText={(value) => onUpdateVariantDraft?.(row.key, { stockQty: value })}
                        placeholder="0"
                        placeholderTextColor={ticketColors.ink3}
                        editable={!row.id}
                        accessibilityLabel={row.id ? 'Stock gestionado desde inventario' : 'Stock inicial'}
                      />
                      {row.id ? (
                        <Text style={[styles.inputHelper, { marginTop: 2 }]}>
                          Editá el stock desde Inventario
                        </Text>
                      ) : null}
                    </View>
                  </View>
                ) : null}

                {/* Collapsible: SKU, barcode, image */}
                <TouchableOpacity
                  onPress={() => toggleVariantExpanded(row.key)}
                  accessibilityRole="button"
                  accessibilityLabel={isExpanded ? 'Ocultar opciones' : 'Ver más opciones'}
                  style={{ paddingVertical: 10, marginTop: 4, minHeight: 40, justifyContent: 'center' }}
                >
                  <Text style={{ fontSize: 12, color: ticketColors.gold }}>
                    {isExpanded ? '▾ Menos opciones' : '▸ Más opciones (SKU, código, imagen)'}
                  </Text>
                </TouchableOpacity>
                {isExpanded ? (
                  <>
                    <View style={styles.variantRow}>
                      <View style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}>
                        <Text style={styles.ticketFormLabel}>SKU</Text>
                        <TextInput
                          style={styles.ticketFormInput}
                          value={row.sku}
                          onChangeText={(value) =>
                            onUpdateVariantDraft?.(row.key, { sku: value.toUpperCase() })
                          }
                          placeholder="Opcional"
                          placeholderTextColor={ticketColors.ink3}
                          autoCapitalize="characters"
                        />
                      </View>
                      <View style={[styles.ticketFormField, styles.variantFieldHalf, { marginBottom: 0 }]}>
                        <Text style={styles.ticketFormLabel}>Código barras</Text>
                        <TextInput
                          style={styles.ticketFormInput}
                          value={row.barcode}
                          onChangeText={(value) =>
                            onUpdateVariantDraft?.(row.key, { barcode: value.toUpperCase() })
                          }
                          placeholder="Opcional"
                          placeholderTextColor={ticketColors.ink3}
                          autoCapitalize="characters"
                        />
                        <TouchableOpacity
                          style={styles.variantGenerateBarcodeButton}
                          onPress={() => {
                            const attrSeed = Object.values(row.attributes).join('-');
                            const seed = `${draft.sku || draft.name || 'variant'}-${attrSeed}-${row.key}`;
                            onUpdateVariantDraft?.(row.key, {
                              barcode: suggestInternalVariantBarcode(seed),
                            });
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Generar código de barras interno"
                        >
                          <Text style={styles.variantGenerateBarcodeButtonText}>Generar código</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                    <View style={styles.variantRow}>
                      <View style={styles.ticketFormField}>
                        <Text style={styles.ticketFormLabel}>Imagen de la variante</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          {row.imageUrl ? (
                            <Image
                              source={{ uri: row.imageUrl }}
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: 8,
                                backgroundColor: ticketColors.bg,
                              }}
                              resizeMode="cover"
                            />
                          ) : (
                            <View
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: 8,
                                backgroundColor: ticketColors.bg,
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Text style={{ fontSize: 9, color: ticketColors.ink3 }}>Sin foto</Text>
                            </View>
                          )}
                          <TouchableOpacity
                            style={[posUi.secondaryButton, { paddingVertical: 6, paddingHorizontal: 10 }]}
                            onPress={() => onPickVariantImage?.(row.key)}
                            disabled={variantImageUploadingKey === row.key}
                            accessibilityRole="button"
                            accessibilityLabel="Subir imagen de la variante"
                          >
                            <Text style={posUi.secondaryButtonText}>
                              {variantImageUploadingKey === row.key
                                ? 'Subiendo…'
                                : row.imageUrl
                                  ? 'Cambiar'
                                  : 'Subir'}
                            </Text>
                          </TouchableOpacity>
                          {row.imageUrl ? (
                            <TouchableOpacity
                              style={[posUi.secondaryButton, { paddingVertical: 6, paddingHorizontal: 10 }]}
                              onPress={() => onRemoveVariantImage?.(row.key)}
                              accessibilityRole="button"
                              accessibilityLabel="Quitar imagen de la variante"
                            >
                              <Text style={posUi.secondaryButtonText}>Quitar</Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                      </View>
                    </View>
                  </>
                ) : null}
              </View>
            );
          })}
        </>
      ) : null}

      {variantDrafts.length > 0 ? (
        <TouchableOpacity
          style={[posUi.secondaryButton, styles.variantAddButton]}
          onPress={() => onAddVariantDraft?.()}
          accessibilityRole="button"
          accessibilityLabel="Agregar variante"
        >
          <Text style={posUi.secondaryButtonText}>+ Agregar variante</Text>
        </TouchableOpacity>
      ) : null}

      {/* #705: variantes desactivadas con botón de reactivar */}
      {inactiveVariants.length > 0 && onReactivateVariant ? (
        <View style={{ marginTop: 16, padding: 12, borderRadius: 8, borderWidth: 1, borderColor: ticketColors.border }}>
          <Text style={{ fontSize: 13, fontWeight: '600', color: ticketColors.ink2, marginBottom: 8 }}>
            Variantes desactivadas
          </Text>
          {inactiveVariants.map((variant) => {
            const label = variant.nameSuffix?.trim()
              || (variant.attributes ? Object.values(variant.attributes).join(' / ') : null)
              || variant.id.slice(0, 8);
            return (
              <View key={variant.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, color: ticketColors.ink }}>{label}</Text>
                  {variant.sku ? <Text style={{ fontSize: 12, color: ticketColors.ink2 }}>SKU: {variant.sku}</Text> : null}
                </View>
                <TouchableOpacity
                  style={[posUi.secondaryButton, { paddingVertical: 6, paddingHorizontal: 12 }]}
                  onPress={() => onReactivateVariant(variant.id)}
                  accessibilityRole="button"
                  accessibilityLabel={`Reactivar variante ${label}`}
                >
                  <Text style={posUi.secondaryButtonText}>Reactivar</Text>
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      ) : null}

      {ConfirmModal}
    </View>
  );
}
